import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  type: string;
  salary: string;
  logo: string;
  alt: string;
  deadline: string;
  postedAt: string;
  featured: boolean;
}

interface JobsSectionProps {
  jobs: Job[];
}

const JobsSection = ({ jobs }: JobsSectionProps) => {
  return (
    <section className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl lg:text-3xl font-heading font-bold text-foreground uppercase tracking-wide mb-2">
            Recent Job Opportunities
          </h2>
          <p className="text-text-secondary">
            Find your next career opportunity in the construction industry
          </p>
        </div>
        <Link
          href="/jobs-board"
          className="flex items-center space-x-2 text-primary hover:text-accent transition-industrial font-heading font-bold uppercase tracking-wide text-sm"
        >
          <span>View All</span>
          <Icon name="ArrowRightIcon" size={16} />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {jobs.map((job) => (
          <div key={job.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-industrial">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                  <AppImage
                    src={job.logo}
                    alt={job.alt}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-card-foreground text-sm uppercase tracking-wide">
                    {job.company}
                  </h3>
                  <p className="text-xs text-text-secondary">{job.postedAt}</p>
                </div>
              </div>
              {job.featured && (
                <span className="bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-heading font-bold uppercase tracking-wide">
                  Featured
                </span>
              )}
            </div>
            
            <h4 className="text-lg font-heading font-bold text-card-foreground mb-3 line-clamp-2 hover:text-primary transition-industrial">
              <Link href={`/job-detail?id=${job.id}`}>
                {job.title}
              </Link>
            </h4>
            
            <div className="space-y-2 text-sm text-text-secondary mb-4">
              <div className="flex items-center space-x-2">
                <Icon name="MapPinIcon" size={14} />
                <span>{job.location}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="BriefcaseIcon" size={14} />
                <span>{job.type}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CurrencyDollarIcon" size={14} />
                <span>{job.salary}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="ClockIcon" size={14} />
                <span>Deadline: {job.deadline}</span>
              </div>
            </div>
            
            <Link
              href={`/job-detail?id=${job.id}`}
              className="w-full bg-primary text-primary-foreground px-4 py-2 rounded text-center font-heading font-bold uppercase tracking-wide text-sm hover:bg-accent transition-industrial block"
            >
              Apply Now
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
};

export default JobsSection;